import * as Sentry from '@sentry/react';
import groupBy from 'lodash/groupBy';

import { actions as boardActions } from 'slices/boardSlice/slice';
import { selectCurrentWorkspaceId } from 'slices/workspacesSlice';
import type StreamUpdatesManager from './streamUpdatesManager';
import { fetchEntityUpdates } from './fetchEntityUpdates';
import { STREAM_ABORTED_ERROR } from './constants';
import {
  PERSONAL_ENTITY_ORDER,
  WORKSPACE_ENTITY_ORDER,
  PERSONAL_SCOPE,
} from './entities';
import type { StreamEntitySlug } from './entities';
import type { AppStore } from '../store/store';
import getDispatchDictionaryUpdate from '../streamUpdate/getDictionaryUpdate';
import getDispatchDictionaryDelete from '../streamUpdate/getDictionaryDelete';
import {
  getStoredUpdateTokens,
  setStoredUpdateTokens,
} from './storage';
import envService from 'src/services/app/envService';
import type {
  EntityTokenMap,
  StreamScopeKey,
  UpdateTokensMap,
  UpdateProcessResult,
} from './types';

type ItemsAccumulator = Map<string | undefined, any>;

const logPrerelease = (...args: unknown[]) => {
  if (envService.isPrerelease()) {
    // eslint-disable-next-line no-console
    console.warn('[streamUpdatesV3]', ...args);
  }
};

const mergeTokens = (
  base: UpdateTokensMap | undefined,
  updates: UpdateTokensMap
): UpdateTokensMap => {
  const result: UpdateTokensMap = { ...(base || {}) };

  Object.entries(updates).forEach(([scope, entityTokens]) => {
    result[scope] = {
      ...(result[scope] || {}),
      ...entityTokens,
    } as EntityTokenMap;
  });

  return result;
};

const dedupeItems = (collector: ItemsAccumulator, items: any[]) => {
  items.forEach((item) => {
    collector.set(item?.item_uuid, item);
  });
};

const fetchScopeEntities = async ({
  entityOrder,
  tokens,
  workspaceId,
  collector,
  manager,
}: {
  entityOrder: StreamEntitySlug[];
  tokens: EntityTokenMap | undefined;
  workspaceId?: string;
  collector: ItemsAccumulator;
  manager: StreamUpdatesManager;
}): Promise<EntityTokenMap> => {
  const newTokens: EntityTokenMap = {};

  const results = await Promise.all(
    entityOrder.map((entity) =>
      fetchEntityUpdates({
        entity,
        initialToken: tokens?.[entity],
        workspaceId,
        manager,
      })
    )
  );

  results.forEach(({ entity, token, items }) => {
    newTokens[entity] = token;

    if (items.length) {
      dedupeItems(collector, items);
    }
  });

  return newTokens;
};

export async function updateProcess({
  tokens,
  store,
  manager,
}: {
  tokens: UpdateTokensMap;
  store: AppStore;
  manager: StreamUpdatesManager;
}): Promise<UpdateProcessResult> {
  if (manager.isClosed) {
    logPrerelease('updateProcess aborted: manager closed');
    return { status: 'aborted', tokens };
  }
  const dispatchDictionaryUpdate = await getDispatchDictionaryUpdate();
  const dispatchDictionaryDelete = await getDispatchDictionaryDelete();

  const state = store.getState();
  const currentWorkspaceId = selectCurrentWorkspaceId(state);

  const collector: ItemsAccumulator = new Map();
  const updatedTokens: UpdateTokensMap = {};

  const scopeKeys = Object.keys(tokens) as StreamScopeKey[];
  logPrerelease('updateProcess start', { scopeKeys, tokens });

  try {
    if (scopeKeys.includes(PERSONAL_SCOPE)) {
      const scopeTokens = await fetchScopeEntities({
        entityOrder: PERSONAL_ENTITY_ORDER,
        tokens: tokens[PERSONAL_SCOPE],
        collector,
        manager,
      });

      updatedTokens[PERSONAL_SCOPE] = scopeTokens;
    }

    const workspaceScopeKeys = scopeKeys.filter((key) => key !== PERSONAL_SCOPE);

    for (const workspaceScope of workspaceScopeKeys) {
      if (!workspaceScope) {
        continue;
      }

      const scopeTokens = await fetchScopeEntities({
        entityOrder: WORKSPACE_ENTITY_ORDER,
        tokens: tokens[workspaceScope],
        workspaceId: workspaceScope,
        collector,
        manager,
      });

      updatedTokens[workspaceScope] = scopeTokens;
    }
  } catch (err) {
    if (err instanceof Error && err.message === STREAM_ABORTED_ERROR) {
      logPrerelease('updateProcess aborted during fetch');
      return { status: 'aborted', tokens };
    }
    Sentry.captureException(err);
    console.warn(err);
    store.dispatch(boardActions.setSyncFailed(true));
    logPrerelease('updateProcess fetch error', err);

    return { status: 'error', tokens: tokens };
  }

  const items = Array.from(collector.values());
  const itemsGroupByType = groupBy(items, 'item_type');

  try {
    const promises: Promise<unknown>[] = [];

    for (const key in itemsGroupByType) {
      const { updated, deleted } = groupBy(itemsGroupByType[key], 'status');

      if (deleted?.length) {
        const deletedItems = deleted.map(({ item }) => item);
        if (dispatchDictionaryDelete[key]) {
          promises.push(dispatchDictionaryDelete[key](deletedItems));
        }
      }

      if (updated?.length) {
        const updatedItems = updated.map(({ item }) => item);
        const updateAction = dispatchDictionaryUpdate[key];

        if (updateAction) {
          promises.push(updateAction(updatedItems));
        }
      }
    }

    await Promise.allSettled(promises);

    const storedTokens = getStoredUpdateTokens();
    const mergedTokens = mergeTokens(storedTokens, updatedTokens);

    setStoredUpdateTokens(mergedTokens);
    logPrerelease('updateProcess success', { mergedTokens });

    return { status: 'success', tokens: mergedTokens };
  } catch (err) {
    console.warn(err);
    logPrerelease('updateProcess reducer error; resetting tokens', err);

    const freshTokens: UpdateTokensMap = {
      [PERSONAL_SCOPE]: {},
    };

    if (currentWorkspaceId) {
      freshTokens[currentWorkspaceId] = {};
    }

    setStoredUpdateTokens(freshTokens);

    return { status: 'error', tokens: freshTokens };
  }
}
